import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Autocomplete,
  Typography,
  Box,
  Chip,
  Grid
} from '@mui/material';
import { axiosService } from 'shared/helpers/axios';
import { deleteBudget } from 'shared/utils/proposalApi';

const BudgetDialog = ({ open, onClose, taskId, budgetItem, onSuccess, consortiumMembers = [], currentUser = null }) => {
  const [formData, setFormData] = useState({
    task: taskId || '',
    requester: null,
    consumables: 0,
    equipment: 0,
    personnel: 0,
    travel: 0,
    subcontracting: 0,
    overheads: 0,
    consumables_justification: '',
    equipment_justification: '',
    personnel_justification: '',
    travel_justification: '',
    subcontracting_justification: '',
    overheads_justification: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (budgetItem) {
      setFormData({
        task: budgetItem.task || taskId,
        requester: consortiumMembers.find(u => u.id === budgetItem.requester) || null,
        consumables: budgetItem.consumables || 0,
        equipment: budgetItem.equipment || 0,
        personnel: budgetItem.personnel || 0,
        travel: budgetItem.travel || 0,
        subcontracting: budgetItem.subcontracting || 0,
        overheads: budgetItem.overheads || 0,
        consumables_justification: budgetItem.consumables_justification || '',
        equipment_justification: budgetItem.equipment_justification || '',
        personnel_justification: budgetItem.personnel_justification || '',
        travel_justification: budgetItem.travel_justification || '',
        subcontracting_justification: budgetItem.subcontracting_justification || '',
        overheads_justification: budgetItem.overheads_justification || ''
      });
    } else {
      // Find current user in consortium members for default requester
      // console.log(  'Current User:', currentUser);
      // console.log(  'Consortium Members:', consortiumMembers);
      const defaultRequester = currentUser ? 
        consortiumMembers.find(u => u.id === currentUser.id || u.email === currentUser.email) : 
        null;
      
      setFormData({
        task: taskId || '',
        requester: defaultRequester || null,
        consumables: 0,
        equipment: 0,
        personnel: 0,
        travel: 0,
        subcontracting: 0,
        overheads: 0,
        consumables_justification: '',
        equipment_justification: '',
        personnel_justification: '',
        travel_justification: '',
        subcontracting_justification: '',
        overheads_justification: ''
      });
    }
  }, [budgetItem, taskId, consortiumMembers, currentUser, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotal = () => {
    return (
      parseFloat(formData.consumables || 0) +
      parseFloat(formData.equipment || 0) +
      parseFloat(formData.personnel || 0) +
      parseFloat(formData.travel || 0) +
      parseFloat(formData.subcontracting || 0) +
      parseFloat(formData.overheads || 0)
    );
  };

  const handleSubmit = async () => {
    if (!formData.requester) {
      setError('Please select a requester');
      return;
    }

    setIsSaving(true);
    setError(null);

    const submitData = {
      ...formData,
      requester: formData.requester.id
    };

    try {
      let response;
      if (budgetItem?.id) {
        response = await axiosService.put(`/proposal/budgets/${budgetItem.id}/`, submitData);
      } else {
        response = await axiosService.post('/proposal/budgets/', submitData);
      }
      
      onSuccess && onSuccess(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save budget');
    }

    setIsSaving(false);
  };

  const handleDelete = async () => {
    if (!budgetItem?.id) return;
    
    if (!window.confirm('Are you sure you want to delete this budget item? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    const result = await deleteBudget(budgetItem.id);
    
    if (result.success) {
      onSuccess && onSuccess();
      onClose();
    } else {
      setError(result.error || 'Failed to delete budget item');
    }

    setIsDeleting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 3000 }}>
      <DialogTitle>{budgetItem ? 'Edit Budget Item' : 'Add Budget Item'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Autocomplete
            options={consortiumMembers}
            value={formData.requester}
            onChange={(e, newValue) => handleChange('requester', newValue)}
            slotProps={{ popper: { style: { zIndex: 3001 } } }}
            getOptionLabel={(option) => {
              if (!option) return '';
              const name = option.name || option.email || option.username || 'Unknown';
              return option.org_short_name ? `${name} (${option.org_short_name})` : name;
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Requester"
                required
                helperText="Must be a task assignee. Add members to the task first if they are missing."
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                if (!option) return null;
                return (
                  <Chip
                    key={option.id || index}
                    label={`${option.name || option.email || 'Unknown'}`}
                    {...getTagProps({ index })}
                    size="small"
                  />
                );
              })
            }
          />

  
<Box>
  <Typography variant="h6" gutterBottom>
    Budget Breakdown (EUR)
  </Typography>

  <Stack spacing={2} sx= {{ pr: 3 }}>
    {/* Consumables */}
    <Grid container spacing={2}  >
      <Grid item xs={12} md={3}>
        <TextField
          label="Consumables (€)"
          type="number"
          value={formData.consumables}
          onChange={(e) => handleChange('consumables', e.target.value)}
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <TextField
          label="Justification"
          value={formData.consumables_justification}
          onChange={(e) => handleChange('consumables_justification', e.target.value)}
          multiline
          minRows={2}
          fullWidth
         
        />
      </Grid>
    </Grid>

    {/* Equipment */}
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <TextField
          label="Equipment (€)"
          type="number"
          value={formData.equipment}
          onChange={(e) => handleChange('equipment', e.target.value)}
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <TextField
          label="Justification"
          value={formData.equipment_justification}
          onChange={(e) => handleChange('equipment_justification', e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </Grid>
    </Grid>

    {/* Personnel */}
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <TextField
          label="Personnel (€)"
          type="number"
          value={formData.personnel}
          onChange={(e) => handleChange('personnel', e.target.value)}
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <TextField
          label="Justification"
          value={formData.personnel_justification}
          onChange={(e) => handleChange('personnel_justification', e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </Grid>
    </Grid>

    {/* Travel */}
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <TextField
          label="Travel (€)"
          type="number"
          value={formData.travel}
          onChange={(e) => handleChange('travel', e.target.value)}
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <TextField
          label="Justification"
          value={formData.travel_justification}
          onChange={(e) => handleChange('travel_justification', e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </Grid>
    </Grid>

    {/* Subcontracting */}
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <TextField
          label="Subcontracting (€)"
          type="number"
          value={formData.subcontracting}
          onChange={(e) => handleChange('subcontracting', e.target.value)}
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <TextField
          label="Justification"
          value={formData.subcontracting_justification}
          onChange={(e) => handleChange('subcontracting_justification', e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </Grid>
    </Grid>

    {/* Overheads */}
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <TextField
          label="Overheads (€)"
          type="number"
          value={formData.overheads}
          onChange={(e) => handleChange('overheads', e.target.value)}
          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <TextField
          label="Justification"
          value={formData.overheads_justification}
          onChange={(e) => handleChange('overheads_justification', e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </Grid>
    </Grid>
  </Stack>
</Box>



<Box
  sx={{
    p: 2,
    mt: 2,
    bgcolor: 'grey.100',
    borderRadius: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }}
>
  <Typography fontWeight={600}>Total Budget</Typography>
  <Typography variant="h6">
    €{calculateTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}
  </Typography>
</Box>

          {error && (
            <Typography color="error">{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {budgetItem?.id && (
          <Button 
            onClick={handleDelete} 
            color="error"
            disabled={isSaving || isDeleting}
            sx={{ mr: 'auto' }}
          >
            {isDeleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        )}
        <Button onClick={onClose} disabled={isSaving || isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving || isDeleting || !formData.requester}
        >
          {isSaving ? <CircularProgress size={24} /> : (budgetItem ? 'Update' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BudgetDialog;
